'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { useChatController } from "./internals/useChatController/index.js";
import { useChatInstance } from "./internals/useChatInstance/index.js";
import { ChatRuntimeContext } from "./internals/useChatRuntimeContext/index.js";
import { defaultPartRenderers } from "./renderers/defaultPartRenderers.js";
import { ChatStoreContext } from "./internals/useChatStoreContext/index.js";

/**
 * Runtime feature flags for the headless chat controller.
 *
 * These flags toggle optional runtime behaviors (not rendering). The Material
 * `ChatBoxFeatures` vocabulary structurally includes these so the flag is
 * declared once and forwarded from `ChatBox` down to the controller.
 */
import { jsx as _jsx } from "react/jsx-runtime";
export function ChatProvider(props) {
  const isActiveConversationIdControlled = Object.prototype.hasOwnProperty.call(props, 'activeConversationId');
  const {
    children,
    adapter,
    onToolCall,
    onFinish,
    onData,
    onError,
    streamFlushInterval,
    partRenderers,
    storeClass,
    features,
    members,
    currentUser,
    roleDisplayNames,
    getMessageAuthorId,
    getMessageAuthorDisplayName,
    getMessageAuthorAvatarUrl,
    messages,
    initialMessages,
    conversations,
    initialConversations,
    activeConversationId,
    initialActiveConversationId,
    composerValue,
    initialComposerValue,
    onMessagesChange,
    onConversationsChange,
    onActiveConversationChange,
    onComposerValueChange
  } = props;
  const parameters = React.useMemo(() => ({
    members,
    currentUser,
    roleDisplayNames,
    getMessageAuthorId,
    getMessageAuthorDisplayName,
    getMessageAuthorAvatarUrl,
    messages,
    initialMessages,
    conversations,
    initialConversations,
    activeConversationId,
    activeConversationIdControlled: isActiveConversationIdControlled,
    initialActiveConversationId,
    composerValue,
    initialComposerValue,
    onMessagesChange,
    onConversationsChange,
    onActiveConversationChange,
    onComposerValueChange
  }), [members, currentUser, roleDisplayNames, getMessageAuthorId, getMessageAuthorDisplayName, getMessageAuthorAvatarUrl, messages, initialMessages, conversations, initialConversations, activeConversationId, isActiveConversationIdControlled, initialActiveConversationId, composerValue, initialComposerValue, onMessagesChange, onConversationsChange, onActiveConversationChange, onComposerValueChange]);
  const store = useChatInstance(parameters, storeClass);
  const actions = useChatController({
    store,
    adapter,
    onToolCall,
    onFinish,
    onData,
    onError,
    streamFlushInterval,
    features
  });
  const mergedPartRenderers = React.useMemo(() => _extends({}, defaultPartRenderers, partRenderers), [partRenderers]);
  const runtimeContextValue = React.useMemo(() => ({
    adapter,
    onToolCall,
    onFinish,
    onData,
    onError,
    partRenderers: mergedPartRenderers,
    actions
  }), [actions, adapter, mergedPartRenderers, onToolCall, onFinish, onData, onError]);
  return /*#__PURE__*/_jsx(ChatStoreContext.Provider, {
    value: store,
    children: /*#__PURE__*/_jsx(ChatRuntimeContext.Provider, {
      value: runtimeContextValue,
      children: children
    })
  });
}